import React, { useEffect, useMemo, useState } from "react";
import { SwiperSlide } from "swiper/react";
import { apiConfiguration } from "../../api-configuration";
import {
  FacebookApi,
  FacebookPostDto,
  ArticleRecommendationDto,
  ContentApi,
  ObjectTypes,
} from "../../lib-api";
import { Navigation, Pagination, Autoplay, Lazy } from "swiper";
import { trackGaEvent } from "../../util/gtm";
import { createRoot } from "react-dom/client";
import { WrappedSwiper } from "./wrapped-swiper";
import { ellipsize } from "../../util/helpers";
import { hookDefaultGATracking } from "../../features/ga-tracking";

const facebookApi = new FacebookApi(apiConfiguration);
const contentApi = new ContentApi(apiConfiguration);

type Post =
  | ({ type: "facebookpost" } & FacebookPostDto)
  | ({ type: "article" } & ArticleRecommendationDto);

type Posts = Post[] | undefined;

const RenderFacebookPost = ({ post, container }: { post: FacebookPostDto, container?: string }) => {
  const containsOffer = useMemo(() => {
    return ["tilbud", "ja tak"].some((x) =>
      post.message?.toLocaleLowerCase().includes(x)
    );
  }, [post.message]);

  return (
    <a
          href={post.permalink ?? ""}
          target="_blank"
          rel="noopener nofollow noreferrer"
          data-businessid={post.businessId}
          data-businessname={post.businessName}
          data-customerid={post.customerId}
          data-customername={post.customerName}
          data-container={container}
    >
      <div>
        <img
          alt="opslagsbillede"
          data-src={post.imageUrl}
          className="swiper-lazy"
        />
        <div className="swiper-lazy-preloader swiper-lazy-preloader-black"></div>
      </div>
      <div className="mx-2 d-flex flex-column">
        <b className="mt-2">{post.businessName}</b>
        <p className="pb-0 flex-grow-1">{ellipsize(post.message ?? "")}</p>
        <small className="text-primary">
          {containsOffer ? "Læs mere om tilbuddet" : "Åbn opslaget"}
        </small>
      </div>
    </a>
  );
};

const RenderArticle = ({ article, container }: { article: ArticleRecommendationDto, container?: string }) => (
  <a
    href={article.articleUrl ?? ""}
    target="_blank"
    rel="noopener nofollow noreferrer"
    data-businessid={article.customerBusiness?.businessId}
    data-businessname={article.customerBusiness?.businessName}
    data-customerid={article.customerBusiness?.customerId}
    data-customername={article.customerBusiness?.customerName}
    data-container={container}

  >
    <div>
      <img
        alt="opslagsbillede"
        data-src={article.assetRetinaSrc}
        className="swiper-lazy"
      />
      <div className="swiper-lazy-preloader swiper-lazy-preloader-black"></div>
    </div>
    <div className="mx-2 d-flex flex-column">
      <b className="mt-2">{article.articleTitle}</b>
      {/* <p className="pb-0 flex-grow-1">{ellipsize(post.message ?? "")}</p> */}
      <div className="flex-grow-1"></div>
      <small className="text-primary">Åbn artiklen</small>
    </div>
  </a>
);

const BusinessPlusCarousel: React.FC<{
  hasProductId: string;
  container?: string;
}> = ({ hasProductId, container }) => {
  const [items, setItems] = useState<Posts>(undefined);

  useEffect(() => {
    (async () => {
      try {
        const [facebookPosts, businessArticles] = await Promise.all([
          facebookApi.getFacebookPosts({
            getFacebookPostsForm: {
              hasProductId: hasProductId,
              maxAgeInDays: 1,
              postsCountPerCustomer: 2,
            },
          }),
          contentApi.getLatestContentFromCustomersWithProduct({
            productId: hasProductId,
          }),
        ]);

        const mappedFacebookPosts: Post[] = facebookPosts.map((x) => ({
          type: "facebookpost",
          ...x,
        }));
        const mappedBusinessArticles: Post[] = businessArticles.map((x) => ({
          type: "article",
          ...x,
        }));

        setItems([...mappedFacebookPosts, ...mappedBusinessArticles]);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Hook up GA tracking for dynamically rendered content
  useEffect(() => {
    if (items && items.length > 0) {
      // Use setTimeout to ensure DOM elements are rendered
      setTimeout(() => {
        const carouselElement = document.querySelector(
          "[data-business-plus-carousel]"
        );
        if (carouselElement) {
          hookDefaultGATracking(carouselElement);
        }
      }, 100);
    }
  }, [items]);

  if (items !== undefined && items.length == 0) {
    return null;
  }

  return (
    <div data-business-plus-carousel>
      {items !== undefined && items.length > 0 && (
        <WrappedSwiper
          modules={[Navigation, Pagination, Lazy, Autoplay]}
          slidesPerView={1}
          breakpoints={{
            "640": {
              slidesPerView: 2,
            },
            "768": {
              slidesPerView: 2,
            },
            "1024": {
              slidesPerView: 3,
            },
          }}
          navigation
          pagination={{ clickable: true }}
          // loop={true}
          // loopedSlides={0}
          // loopedSlidesLimit={true}
          preloadImages={false}
          lazy={{ enabled: true, checkInView: false, loadPrevNext: true }}
          //autoplay={{ delay: 2500, disableOnInteraction: false }}
        >
          <>
            {items.map((item, index) => (
              <SwiperSlide key={`${item.type}-${index}`}>
                {item.type === "facebookpost" && (
                  <div
                    ga-track-impression=""
                    data-itemid={item.id}
                    data-itemtype={ObjectTypes.FacebookPost}
                    data-container={container}
                    data-businessid={item.businessId}
                    data-businessname={item.businessName}
                    data-customerid={item.customerId}
                    data-customername={item.customerName}

                        >
                            <RenderFacebookPost post={item} container={container} />
                  </div>
                )}
                {item.type === "article" && (
                  <div
                    ga-track-impression=""
                    data-itemid={item.contentId}
                    data-itemtype={ObjectTypes.Content}
                    data-container={container}
                    data-businessid={item.customerBusiness?.businessId}
                    data-businessname={item.customerBusiness?.businessName}
                    data-customerid={item.customerBusiness?.customerId}
                    data-customername={item.customerBusiness?.customerName}
                  >
                            <RenderArticle article={item} container={container} />
                  </div>
                )}
              </SwiperSlide>
            ))}
          </>
        </WrappedSwiper>
      )}
      <small className="d-block text-right">Annoncørbetalt indhold</small>
    </div>
  );
};

const carousels = document.getElementsByTagName("business-plus-carousel");

for (let i = 0; i < carousels.length; i++) {
  const element = carousels.item(i) as HTMLElement;
  const root = createRoot(element);

  const hasProductId = element.dataset.hasProductId!;
  const container = element.dataset.container;

  root.render(
    <BusinessPlusCarousel hasProductId={hasProductId} container={container} />
  );
}
